import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Zod from 'zod';
import { DateTime } from 'luxon';
import { useMutation, useApolloClient } from '@apollo/client/react';
import { Box } from '@mui/material';

import { Button } from 'common/components/Button';
import { DatePickerField } from 'common/forms/Field';
import {
    IReceivedCertificateFragment,
    UpdateReceivedCertificatesDocument,
} from 'graphql/types';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { dateTimeValidator } from 'common/validators/dateTime';

type TDatesFormValues = {
    issueDate?: DateTime | null;
    expiryDate?: DateTime | null;
};

interface IProps {
    receivedCertificate: IReceivedCertificateFragment;
}

export const ReceivedCertificateDatesForm = ({
    receivedCertificate,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const client = useApolloClient();
    const [updateReceivedCertificates] = useMutation(
        UpdateReceivedCertificatesDocument
    );

    const { issueDate, expiryDate } = receivedCertificate;

    const validationSchema = Zod.object({
        issueDate: dateTimeValidator.optional().nullable(),
        expiryDate: dateTimeValidator.optional().nullable(),
    });

    const { control, handleSubmit, reset, formState } =
        useForm<TDatesFormValues>({
            resolver: zodResolver(validationSchema),
            defaultValues: {
                issueDate: issueDate ? DateTime.fromISO(issueDate) : null,
                expiryDate: expiryDate ? DateTime.fromISO(expiryDate) : null,
            },
        });

    useEffect(() => {
        reset({
            issueDate: issueDate ? DateTime.fromISO(issueDate) : null,
            expiryDate: expiryDate ? DateTime.fromISO(expiryDate) : null,
        });
    }, [issueDate, expiryDate, reset]);

    const handleUpdate = async (values: {
        issueDate?: DateTime | null;
        expiryDate?: DateTime | null;
    }) => {
        if (!receivedCertificate) return;

        try {
            await updateReceivedCertificates({
                variables: {
                    ids: [receivedCertificate.id],
                    receivedCertificate: {
                        issueDate: values.issueDate?.toISO(),
                        expiryDate: values.expiryDate?.toISO(),
                    },
                },
            });

            // Update the cache manually instead of evicting
            client.cache.modify({
                id: client.cache.identify(receivedCertificate),
                fields: {
                    issueDate() {
                        return values.issueDate?.toISO() ?? null;
                    },
                    expiryDate() {
                        return values.expiryDate?.toISO() ?? null;
                    },
                },
            });

            displaySnackbar(translate('receivedCertificate.updateSuccess'), {
                variant: 'success',
            });
        } catch (error) {
            showApolloError(error);
        }
    };

    const { isSubmitting, isDirty } = formState;

    return (
        <form onSubmit={handleSubmit(handleUpdate)}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    mt: 1,
                }}
            >
                <DatePickerField
                    control={control}
                    label={translate('certificateGenerateDialog.issueDate')}
                    name="issueDate"
                    slotProps={{
                        textField: { margin: 'none' },
                    }}
                />

                <DatePickerField
                    control={control}
                    label={translate('certificateGenerateDialog.expiryDate')}
                    name="expiryDate"
                    slotProps={{
                        textField: { margin: 'none' },
                    }}
                />

                <Button
                    color="primary"
                    disabled={isSubmitting || !isDirty}
                    size="medium"
                    sx={{ height: 55 }}
                    type="submit"
                    variant="contained"
                >
                    {translate('update')}
                </Button>
            </Box>
        </form>
    );
};
